import React from 'react';
import { Dialog, Box, Typography, Divider } from '@mui/material';
import { DialogHeader } from './AppDialog';
import CategoryChip from './CategoryChip';
import StatusChip from './StatusChip';
import { useData } from '../../contexts/DataContext';

/*
  TransactionDetailDialog: compact read-only dialog with the full details of a
  single transaction — opened by clicking a row in the Overview "Recent" card
  (and reusable anywhere a transaction row wants a details popup).

  Layout: a hero block (big signed amount over the description) followed by
  label/value rows for the structured fields. Notes get a stacked block so
  long text reads naturally instead of being squeezed right-aligned.

  Props: open, onClose, transaction ({ description, amount, date, category,
  account, isPending, notes }). Category/account names are resolved here via
  useData so callers only need the raw transaction.
*/

const DetailRow = ({ label, children }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, py: 1.1 }}>
    <Typography
      variant="caption"
      sx={{ color: 'text.secondary', fontWeight: 700, flexShrink: 0 }}
    >
      {label}
    </Typography>
    <Box sx={{ minWidth: 0, textAlign: 'right' }}>{children}</Box>
  </Box>
);

const TransactionDetailDialog = ({ open, onClose, transaction }) => {
  const { categories, accounts } = useData();

  if (!transaction) return null;

  const category = categories.find((c) => c.id === transaction.category);
  const account = accounts.find((a) => a.id === transaction.account);
  const isPositive = transaction.amount > 0;

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Math.abs(amount));
  const longDate = new Date(transaction.date).toLocaleDateString('en-NZ', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogHeader title="Transaction Details" onClose={onClose} />

      <Box sx={{ px: 2.5, pb: 2.5 }}>
        {/* Hero: signed amount over the description */}
        <Box sx={{ textAlign: 'center', pt: 0.5, pb: 1.5 }}>
          <Typography
            sx={{
              fontSize: '1.9rem',
              fontWeight: 800,
              letterSpacing: '-0.02em',
              lineHeight: 1.2,
              color: isPositive ? 'success.main' : 'error.main',
            }}
          >
            {isPositive ? '+' : '−'}{formatCurrency(transaction.amount)}
          </Typography>
          <Typography variant="subtitle2" sx={{ mt: 0.5, overflowWrap: 'anywhere' }}>
            {transaction.description || 'Untitled transaction'}
          </Typography>
          {transaction.isPending && (
            <StatusChip status="warning" label="Pending" sx={{ mt: 1, height: 20, fontSize: '0.75rem' }} />
          )}
        </Box>

        <Divider />

        <DetailRow label="Date">
          <Typography variant="body2" fontWeight={600}>{longDate}</Typography>
        </DetailRow>
        <Divider />

        <DetailRow label="Category">
          <CategoryChip
            label={category?.name || 'Uncategorized'}
            color={category?.color}
            size="small"
            sx={{ height: 22, fontSize: '0.75rem', maxWidth: 240 }}
          />
        </DetailRow>
        <Divider />

        <DetailRow label="Account">
          <Typography variant="body2" fontWeight={600} sx={{ overflowWrap: 'anywhere' }}>
            {account?.name || 'Unknown Account'}
          </Typography>
        </DetailRow>

        {transaction.notes && (
          <>
            <Divider />
            <Box sx={{ pt: 1.1 }}>
              <Typography
                variant="caption"
                sx={{ color: 'text.secondary', fontWeight: 700 }}
              >
                Notes
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                {transaction.notes}
              </Typography>
            </Box>
          </>
        )}
      </Box>
    </Dialog>
  );
};

export default TransactionDetailDialog;
